<script setup lang="ts">
import { ref } from "vue";
import type { GroupSummary } from "../../domain/store.js";

/**
 * Group list and creation, PRD FR-1.1 and FR-1.2.
 *
 * This is the app's home. Each row shows what the PRD asks for: how many
 * players, how many games have been played, and whether one is in progress.
 */

defineProps<{ groups: GroupSummary[]; rejected: number }>();

const emit = defineEmits<{
  create: [name: string];
  open: [groupId: string];
  remove: [groupId: string];
  exportData: [];
  importData: [file: File];
  allTime: [];
  restore: [];
  rules: [];
  retryRejected: [];
  clearRejected: [];
}>();

function pickFile(event: Event): void {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) emit("importData", file);
  // Clear it, or picking the same file twice in a row does nothing.
  input.value = "";
}

const draft = ref("");
const confirming = ref<string | null>(null);

function create(): void {
  const name = draft.value.trim();
  if (!name) return;
  emit("create", name);
  draft.value = "";
}
</script>

<template>
  <div class="screen">
    <header class="head">
      <div class="head-row">
        <h1>Dänisch Kniffel</h1>
        <button
          class="rules-link"
          @click="emit('rules')"
        >
          Regeln
        </button>
      </div>
      <p class="sub">
        Deine Runden
      </p>
    </header>

    <!-- Turns the server refused, usually because they were entered before it
         knew about the game. Retrying almost always clears them now. -->
    <div
      v-if="rejected > 0"
      class="rejected"
    >
      <div class="rejected-text">
        <strong>{{ rejected }} {{ rejected === 1 ? "Eintrag wurde" : "Einträge wurden" }} nicht gesendet</strong>
        <span>Meist gelöst, indem du es erneut versuchst.</span>
      </div>
      <div class="rejected-actions">
        <button
          class="retry"
          @click="emit('retryRejected')"
        >
          Erneut senden
        </button>
        <button
          class="clear"
          @click="emit('clearRejected')"
        >
          Verwerfen
        </button>
      </div>
    </div>

    <form
      class="add"
      @submit.prevent="create"
    >
      <input
        v-model="draft"
        class="input"
        type="text"
        placeholder="Name der Runde"
        autocomplete="off"
        enterkeyhint="done"
        aria-label="Name der Gruppe"
      >
      <button
        class="add-btn"
        type="submit"
        :disabled="draft.trim() === ''"
      >
        Anlegen
      </button>
    </form>

    <p
      v-if="groups.length === 0"
      class="empty"
    >
      Noch keine Runde. Leg oben eine an — zum Beispiel „Freitagsrunde“.
    </p>

    <ul class="list">
      <li
        v-for="row in groups"
        :key="row.group.id"
        class="row"
      >
        <button
          class="open"
          @click="emit('open', row.group.id)"
        >
          <span class="name">{{ row.group.name }}</span>
          <span class="meta">
            {{ row.players }} {{ row.players === 1 ? "Spieler" : "Spieler" }}
            · {{ row.games }} {{ row.games === 1 ? "Spiel" : "Spiele" }}
          </span>
          <span
            v-if="row.live"
            class="live"
          >läuft</span>
        </button>
        <button
          class="del"
          :aria-label="`${row.group.name} löschen`"
          @click="confirming = row.group.id"
        >
          <svg
            class="ico"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="m7 7 10 10M17 7 7 17" />
          </svg>
        </button>
      </li>
    </ul>

    <!-- FR-7.1: everything, across every group. -->
    <button
      v-if="groups.length > 0"
      class="alltime"
      @click="emit('allTime')"
    >
      <span>🏆 Ewige Tabelle</span>
      <span class="chev">›</span>
    </button>

    <!-- FR-8: the whole thing in and out as one JSON file. -->
    <div class="io">
      <button
        class="io-btn"
        @click="emit('exportData')"
      >
        Daten sichern
      </button>
      <button
        class="io-btn"
        @click="emit('restore')"
      >
        Vom Server holen
      </button>
      <label class="io-btn">
        Daten laden
        <input
          type="file"
          accept="application/json,.json"
          class="file"
          @change="pickFile"
        >
      </label>
    </div>

    <!-- FR-1.5: deleting a group takes its games with it, so it asks first. -->
    <div
      v-if="confirming"
      class="sheet"
    >
      <div class="sheet-card">
        <p class="sheet-text">
          Runde und alle gespeicherten Spiele löschen?
        </p>
        <div class="sheet-actions">
          <button
            class="secondary"
            @click="confirming = null"
          >
            Abbrechen
          </button>
          <button
            class="danger"
            @click="
              emit('remove', confirming);
              confirming = null;
            "
          >
            Löschen
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.screen {
  height: 100dvh;
  display: flex;
  flex-direction: column;
  padding: calc(var(--safe-top) + 12px) 0 calc(var(--safe-bottom) + 12px);
  padding-left: max(16px, var(--safe-left));
  padding-right: max(16px, var(--safe-right));
}

.head-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.head h1 {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
}

.rules-link {
  border: none;
  background: none;
  color: var(--accent);
  font-size: 15px;
  font-weight: 600;
  padding: 8px 2px;
  min-height: 44px;
}

.sub {
  margin: 2px 0 14px;
  color: var(--text-2);
  font-size: 14px;
}

.add {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.input {
  flex: 1;
  min-width: 0;
  border: none;
  border-radius: var(--radius-control);
  background: var(--card);
  color: var(--text);
  /* 16px or more, or iOS zooms the page when the field takes focus. */
  font-size: 16px;
  padding: 12px 14px;
  min-height: 44px;
}

.add-btn {
  border: none;
  border-radius: var(--radius-control);
  background: var(--accent-tint);
  color: var(--accent);
  font-weight: 600;
  padding: 0 14px;
  min-height: 44px;
}

.add-btn:disabled {
  opacity: 0.4;
}

.empty {
  color: var(--text-2);
  font-size: 14px;
  line-height: 1.5;
  margin: 8px 2px;
}

.rejected {
  background: color-mix(in srgb, var(--danger) 12%, var(--card));
  border-radius: var(--radius-card);
  padding: 12px 14px;
  margin-bottom: 12px;
}

.rejected-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
}

.rejected-text strong {
  font-size: 14px;
}

.rejected-text span {
  color: var(--text-2);
}

.rejected-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.retry,
.clear {
  border: none;
  border-radius: var(--radius-control);
  min-height: 40px;
  padding: 0 14px;
  font-size: 14px;
  font-weight: 600;
}

.retry {
  background: var(--accent);
  color: #fff;
}

.clear {
  background: transparent;
  color: var(--text-2);
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.row {
  display: flex;
  align-items: stretch;
  gap: 6px;
  margin-bottom: 6px;
}

.open {
  flex: 1;
  min-width: 0;
  text-align: left;
  border: none;
  background: var(--card);
  border-radius: var(--radius-card);
  padding: 10px 14px;
  min-height: 56px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

.name {
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta {
  font-size: 12px;
  color: var(--text-2);
  margin-top: 1px;
}

.live {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  font-weight: 600;
  color: var(--accent);
  background: var(--accent-tint);
  border-radius: 999px;
  padding: 3px 9px;
}

.del {
  border: none;
  background: var(--card);
  border-radius: var(--radius-card);
  color: var(--text-3);
  width: 48px;
  display: grid;
  place-items: center;
}

.ico {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
}

.alltime {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border: none;
  background: var(--accent-tint);
  color: var(--accent);
  border-radius: var(--radius-card);
  padding: 0 14px;
  min-height: 52px;
  font-size: 15px;
  font-weight: 600;
  margin-top: 6px;
}

.chev {
  font-size: 20px;
  line-height: 1;
}

.io {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding-top: 10px;
}

.io-btn {
  border: none;
  text-align: center;
  border-radius: var(--radius-control);
  background: var(--card);
  color: var(--accent);
  font-size: 13px;
  font-weight: 600;
  min-height: 48px;
  display: grid;
  place-items: center;
  cursor: pointer;
}

/* The label is the button; the input itself is never shown. */
.file {
  display: none;
}

.sheet {
  position: fixed;
  inset: 0;
  background: rgb(0 0 0 / 0.35);
  display: flex;
  align-items: flex-end;
  z-index: 40;
  padding: 16px;
  padding-bottom: calc(16px + var(--safe-bottom));
}

.sheet-card {
  width: 100%;
  background: var(--card);
  border-radius: var(--radius-card);
  padding: 16px;
}

.sheet-text {
  margin: 0 0 12px;
  font-size: 15px;
}

.sheet-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.secondary,
.danger {
  border: none;
  border-radius: var(--radius-control);
  min-height: 48px;
  font-size: 16px;
  font-weight: 600;
}

.secondary {
  background: var(--bg);
  color: var(--accent);
}

.danger {
  background: var(--danger);
  color: #fff;
}
</style>
